import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getWorkOrders,
  getWorkOrder,
  getWorkOrderSummary,
  createWorkOrder,
  updateWorkOrder,
  deleteWorkOrder,
  submitWorkOrder,
  confirmWorkOrder,
  startWorkOrder,
  completeWorkOrder,
  cancelWorkOrder,
  createWorkOrderFromQuote,
  getWorkOrderItems,
  addWorkOrderItem,
  updateWorkOrderItem,
  removeWorkOrderItem,
  getWorkOrderSerials,
  assignSerialToWorkOrder,
  unassignSerialFromWorkOrder,
} from '../api';
import type {
  WorkOrderFilters,
  CreateWorkOrderInput,
  UpdateWorkOrderInput,
  AddWorkOrderItemInput,
  UpdateWorkOrderItemInput,
  AssignSerialInput,
  CreateFromQuoteInput,
} from '../types';
import { wmsInventoryKeys } from './useWmsInventory';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsWorkOrderKeys = {
  all: ['wms-work-orders'] as const,
  // No-arg call returns a prefix so invalidateQueries matches filtered lists.
  list: (filters?: WorkOrderFilters) => filters !== undefined
    ? [...wmsWorkOrderKeys.all, 'list', filters] as const
    : [...wmsWorkOrderKeys.all, 'list'] as const,
  detail: (id: string) => [...wmsWorkOrderKeys.all, 'detail', id] as const,
  summary: () => [...wmsWorkOrderKeys.all, 'summary'] as const,
  items: (workOrderId: string) => [...wmsWorkOrderKeys.all, 'items', workOrderId] as const,
  serials: (workOrderId: string) => [...wmsWorkOrderKeys.all, 'serials', workOrderId] as const,
};

// ============================================================================
// Work Order Hooks
// ============================================================================

export function useWorkOrders(filters: WorkOrderFilters = {}) {
  return useQuery({
    queryKey: wmsWorkOrderKeys.list(filters),
    queryFn: () => getWorkOrders(filters),
  });
}

export function useWorkOrder(id: string) {
  return useQuery({
    queryKey: wmsWorkOrderKeys.detail(id),
    queryFn: () => getWorkOrder(id),
    enabled: !!id,
  });
}

export function useWorkOrderSummary() {
  return useQuery({
    queryKey: wmsWorkOrderKeys.summary(),
    queryFn: getWorkOrderSummary,
  });
}

export function useCreateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkOrderInput) => createWorkOrder(data),
    onSuccess: (workOrder) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success(`Work order "${workOrder.work_order_number}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create work order', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkOrderInput }) => updateWorkOrder(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Work order updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update work order', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWorkOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Work order deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete work order', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Work Order Status Transition Hooks
// ============================================================================

export function useSubmitWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => submitWorkOrder(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Work order submitted for approval');
    },
    onError: (error) => {
      toast.error('Failed to submit work order', { description: getErrorMessage(error) });
    },
  });
}

export function useConfirmWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => confirmWorkOrder(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      // Inventory might have been reserved
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.items() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.summary() });
      toast.success('Work order confirmed');
    },
    onError: (error) => {
      toast.error('Failed to confirm work order', { description: getErrorMessage(error) });
    },
  });
}

export function useStartWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => startWorkOrder(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Work order started');
    },
    onError: (error) => {
      toast.error('Failed to start work order', { description: getErrorMessage(error) });
    },
  });
}

export function useCompleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => completeWorkOrder(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success('Work order completed');
    },
    onError: (error) => {
      toast.error('Failed to complete work order', { description: getErrorMessage(error) });
    },
  });
}

export function useCancelWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => cancelWorkOrder(id, reason),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      // Also invalidate serialized items as they may have been released
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      toast.success('Work order cancelled');
    },
    onError: (error) => {
      toast.error('Failed to cancel work order', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Quote Integration Hooks
// ============================================================================

export function useCreateWorkOrderFromQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, data }: { quoteId: string; data: CreateFromQuoteInput }) =>
      createWorkOrderFromQuote(quoteId, data),
    onSuccess: (workOrder) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.list() });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.summary() });
      toast.success(`Work order "${workOrder.work_order_number}" created from quote`);
    },
    onError: (error) => {
      toast.error('Failed to create work order from quote', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Work Order Items Hooks
// ============================================================================

export function useWorkOrderItems(workOrderId: string) {
  return useQuery({
    queryKey: wmsWorkOrderKeys.items(workOrderId),
    queryFn: () => getWorkOrderItems(workOrderId),
    enabled: !!workOrderId,
  });
}

export function useAddWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, data }: { workOrderId: string; data: AddWorkOrderItemInput }) =>
      addWorkOrderItem(workOrderId, data),
    onSuccess: (item, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      toast.success(`Item "${item.sku_code}" added to work order`);
    },
    onError: (error) => {
      toast.error('Failed to add item', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, itemId, data }: { workOrderId: string; itemId: string; data: UpdateWorkOrderItemInput }) =>
      updateWorkOrderItem(workOrderId, itemId, data),
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      toast.success('Item updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update item', { description: getErrorMessage(error) });
    },
  });
}

export function useRemoveWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, itemId }: { workOrderId: string; itemId: string }) =>
      removeWorkOrderItem(workOrderId, itemId),
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      toast.success('Item removed from work order');
    },
    onError: (error) => {
      toast.error('Failed to remove item', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Work Order Serial Allocation Hooks
// ============================================================================

export function useWorkOrderSerials(workOrderId: string) {
  return useQuery({
    queryKey: wmsWorkOrderKeys.serials(workOrderId),
    queryFn: () => getWorkOrderSerials(workOrderId),
    enabled: !!workOrderId,
  });
}

export function useAssignSerial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, data }: { workOrderId: string; data: AssignSerialInput }) =>
      assignSerialToWorkOrder(workOrderId, data),
    onSuccess: (allocation, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.serials(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      // Invalidate serialized items as their status changed
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedSummary() });
      // Invalidate movements as a new movement was created
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success(`Serial "${allocation.serial_number}" assigned`);
    },
    onError: (error) => {
      toast.error('Failed to assign serial', { description: getErrorMessage(error) });
    },
  });
}

export function useUnassignSerial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, allocationId }: { workOrderId: string; allocationId: string }) =>
      unassignSerialFromWorkOrder(workOrderId, allocationId),
    onSuccess: (_, { workOrderId }) => {
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.serials(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.items(workOrderId) });
      queryClient.invalidateQueries({ queryKey: wmsWorkOrderKeys.detail(workOrderId) });
      // Invalidate serialized items as their status changed
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serialized() });
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.serializedSummary() });
      // Invalidate movements as a new movement was created
      queryClient.invalidateQueries({ queryKey: wmsInventoryKeys.movements() });
      toast.success('Serial unassigned');
    },
    onError: (error) => {
      toast.error('Failed to unassign serial', { description: getErrorMessage(error) });
    },
  });
}
